import React, { useState, useEffect } from 'react';
import './LandingPage.css';

function LandingPage({ data, onButtonClick, onFormSubmit, globalStyles, viewMode, generateGradientCSS }) {
  const [isVisible, setIsVisible] = useState({});
  
  // Estados para seção de contato
  const [contactFormData, setContactFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  // Estados para seção FAQ
  const [openFaq, setOpenFaq] = useState(null);

  // Intersection Observer otimizado para melhor performance
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      {
        threshold: 0.2, // Aumentado para reduzir triggers desnecessários
        rootMargin: '100px' // Aumentado para melhor performance
      }
    );

    // Observar todas as seções com debounce
    const sections = document.querySelectorAll('.section');
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, [data]);

  // Função para gerar background baseado no tipo
  const getBackgroundStyle = (backgroundColor, backgroundType, gradientColors, gradientDirection = '45deg') => {
    if (backgroundType === 'gradient' && gradientColors && gradientColors.length >= 2) {
      return generateGradientCSS(gradientColors, gradientDirection);
    }
    return backgroundColor || '#ffffff';
  };

  // Função para obter estilos de posicionamento da logo
  const getLogoPositionStyles = (position) => {
    const baseStyles = {
      display: 'block',
      marginBottom: '20px'
    };

    switch (position) {
      case 'left':
        return { ...baseStyles, marginLeft: '0', marginRight: 'auto' };
      case 'right':
        return { ...baseStyles, marginLeft: 'auto', marginRight: '0' };
      case 'center':
      default:
        return { ...baseStyles, marginLeft: 'auto', marginRight: 'auto' };
    }
  };

  // Handlers para formulário de contato
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      if (onFormSubmit) {
        const success = await onFormSubmit(contactFormData, 'contact');
        if (success) {
          setSubmitMessage('Mensagem enviada com sucesso!');
          setContactFormData({});
          
          // Se tiver WhatsApp, redirecionar
          const contactSection = data.sections.find(s => s.type === 'contact');
          const whatsappNumber = contactSection?.content?.whatsappNumber;
          if (whatsappNumber) {
            const message = encodeURIComponent(
              `Olá! Vim através do site.\n\nNome: ${contactFormData.name || ''}\nEmail: ${contactFormData.email || ''}\nMensagem: ${contactFormData.message || ''}`
            );
            window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
          }
        } else {
          setSubmitMessage('Erro ao enviar mensagem. Tente novamente.');
        }
      }
    } catch (error) {
      setSubmitMessage('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler para FAQ
  const toggleFaq = (faqIndex) => {
    setOpenFaq(openFaq === faqIndex ? null : faqIndex);
  };

  // CORRIGIDA: Renderizar seção Hero sem textos padrão
  const renderHeroSection = (section, index) => {
    const { content, styles } = section;
    const { title, subtitle, logo, buttons, backgroundImage, backgroundColor, backgroundType, gradientColors, gradientDirection, textColor } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    // Escolher imagem baseada no viewMode
    const backgroundImg = viewMode === 'mobile' && backgroundImage?.mobile 
      ? backgroundImage.mobile 
      : backgroundImage?.desktop;

    const sectionStyles = {
      background: backgroundImg 
        ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${backgroundImg})`
        : getBackgroundStyle(backgroundColor, backgroundType, gradientColors, gradientDirection),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: textColor || '#ffffff',
      ...styles
    };

    const handleButtonClick = (button, buttonIndex) => {
      if (onButtonClick) {
        onButtonClick(button.text, button.link, 'hero', buttonIndex);
      }
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section hero-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="hero-overlay">
          <div className="container">
            <div className="hero-content">
              {/* Renderização da Logo */}
              {logo?.src && (
                <img 
                  src={logo.src}
                  alt={logo.alt || 'Logo'}
                  className={`hero-logo position-${logo.position || 'center'}`}
                  style={{
                    width: logo.width || '150px',
                    height: logo.height || 'auto',
                    ...getLogoPositionStyles(logo.position || 'center'),
                    maxWidth: '100%',
                    transition: 'all 0.3s ease'
                  }}
                />
              )}

              {/* CORRIGIDO: Só renderiza título se não estiver vazio */}
              {title && title.trim() && (
                <h1 className="hero-title" style={{ color: content.titleStyle?.color || textColor }}>
                  {title}
                </h1>
              )}

              {/* CORRIGIDO: Só renderiza subtítulo se não estiver vazio */}
              {subtitle && subtitle.trim() && (
                <p className="hero-subtitle" style={{ color: content.subtitleStyle?.color || textColor }}>
                  {subtitle}
                </p>
              )}
              
              {/* Múltiplos botões */}
              {buttons && buttons.length > 0 && (
                <div className="hero-buttons">
                  {buttons.map((button, buttonIndex) => (
                    <button 
                      key={button.id || buttonIndex}
                      className="hero-cta btn"
                      onClick={() => handleButtonClick(button, buttonIndex)}
                      style={{
                        background: getBackgroundStyle(
                          button.style?.backgroundColor, 
                          button.style?.backgroundType, 
                          button.style?.gradientColors
                        ),
                        color: button.style?.color || '#ffffff',
                        borderRadius: button.style?.borderRadius || '8px',
                        padding: button.style?.padding || '12px 24px',
                        fontSize: button.style?.fontSize || '16px',
                        fontWeight: button.style?.fontWeight || '600',
                        border: button.style?.border || 'none',
                        margin: '8px'
                      }}
                    >
                      {button.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  };

  // CORRIGIDA: Renderizar seção de recursos sem textos padrão
  const renderFeaturesSection = (section, index) => {
    const { content, styles } = section;
    const { title, subtitle, features } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      ...styles
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section features-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="container">
          <div className="features-content">
            {/* CORRIGIDO: Só renderiza título se não estiver vazio */}
            {title && title.trim() && (
              <h2 className="features-title" style={{ color: content.titleStyle?.color || '#333333' }}>
                {title}
              </h2>
            )}

            {/* CORRIGIDO: Só renderiza subtítulo se não estiver vazio */}
            {subtitle && subtitle.trim() && (
              <p className="features-subtitle" style={{ color: content.subtitleStyle?.color || '#333333' }}>
                {subtitle}
              </p>
            )}
            
            {features && features.length > 0 && (
              <div className="features-grid">
                {features.map((feature, featureIndex) => (
                  <div 
                    key={featureIndex} 
                    className="feature-item"
                    style={{
                      background: getBackgroundStyle(
                        feature.backgroundColor, 
                        feature.backgroundType, 
                        feature.gradientColors
                      ),
                      color: feature.textColor || '#333333'
                    }}
                  >
                    <div className="feature-icon">{feature.icon || '⭐'}</div>
                    <h3 className="feature-title" style={{ color: feature.textColor }}>
                      {feature.title}
                    </h3>
                    <p className="feature-description" style={{ color: feature.textColor }}>
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // CORRIGIDA: Renderizar seção de depoimentos sem textos padrão
  const renderTestimonialsSection = (section, index) => {
    const { content, styles } = section;
    const { title, testimonials } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      ...styles
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section testimonials-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="container">
          <div className="testimonials-content">
            {/* CORRIGIDO: Só renderiza título se não estiver vazio */}
            {title && title.trim() && (
              <h2 className="testimonials-title" style={{ color: content.titleStyle?.color || '#333333' }}>
                {title}
              </h2>
            )}
            
            {testimonials && testimonials.length > 0 && (
              <div className="testimonials-grid">
                {testimonials.map((testimonial, testimonialIndex) => (
                  <div 
                    key={testimonialIndex} 
                    className="testimonial-item"
                    style={{
                      background: getBackgroundStyle(
                        testimonial.backgroundColor, 
                        testimonial.backgroundType, 
                        testimonial.gradientColors
                      ),
                      color: testimonial.textColor || '#333333'
                    }}
                  >
                    <div className="testimonial-content" style={{ color: testimonial.textColor }}>
                      "{testimonial.content}"
                    </div>
                    <div className="testimonial-author">
                      {testimonial.avatar && (
                        <img 
                          src={testimonial.avatar} 
                          alt={testimonial.name} 
                          className="author-avatar" 
                        />
                      )}
                      <div className="author-info">
                        <div className="author-name" style={{ color: testimonial.textColor }}>
                          {testimonial.name}
                        </div>
                        <div className="author-role" style={{ color: testimonial.textColor, opacity: 0.8 }}>
                          {testimonial.role}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // CORRIGIDA: Renderizar seção de preços sem textos padrão
  const renderPricingSection = (section, index) => {
    const { content, styles } = section;
    const { title, subtitle, plans } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      ...styles
    };

    const handlePlanClick = (plan, planIndex) => {
      if (onButtonClick) {
        onButtonClick(plan.button?.text || 'Começar', plan.button?.link || '#', 'pricing', planIndex);
      }
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section pricing-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="container">
          <div className="pricing-content">
            {/* CORRIGIDO: Só renderiza título se não estiver vazio */}
            {title && title.trim() && (
              <h2 className="pricing-title" style={{ color: content.titleStyle?.color || '#333333' }}>
                {title}
              </h2>
            )}

            {/* CORRIGIDO: Só renderiza subtítulo se não estiver vazio */}
            {subtitle && subtitle.trim() && (
              <p className="pricing-subtitle" style={{ color: content.subtitleStyle?.color || '#333333' }}>
                {subtitle}
              </p>
            )}
            
            {plans && plans.length > 0 && (
              <div className="pricing-grid">
                {plans.map((plan, planIndex) => (
                  <div 
                    key={planIndex} 
                    className={`pricing-plan ${plan.highlighted ? 'highlighted' : ''}`}
                    style={{
                      background: getBackgroundStyle(
                        plan.backgroundColor, 
                        plan.backgroundType, 
                        plan.gradientColors
                      ),
                      color: plan.textColor || '#333333'
                    }}
                  >
                    {plan.highlighted && (
                      <div className="plan-badge" style={{ backgroundColor: globalStyles?.primaryColor }}>
                        Mais Popular
                      </div>
                    )}
                    
                    <div className="plan-header">
                      <h3 className="plan-name" style={{ color: plan.textColor }}>
                        {plan.name}
                      </h3>
                      <div className="plan-price">
                        <span className="price" style={{ color: plan.textColor }}>
                          {plan.price}
                        </span>
                        <span className="period" style={{ color: plan.textColor, opacity: 0.8 }}>
                          {plan.period}
                        </span>
                      </div>
                    </div>
                    
                    <ul className="plan-features">
                      {plan.features?.map((feature, featureIndex) => (
                        <li key={featureIndex} style={{ color: plan.textColor }}>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <button 
                      className="plan-cta btn"
                      onClick={() => handlePlanClick(plan, planIndex)}
                      style={{
                        background: getBackgroundStyle(
                          plan.button?.style?.backgroundColor, 
                          plan.button?.style?.backgroundType, 
                          plan.button?.style?.gradientColors
                        ),
                        color: plan.button?.style?.color || '#ffffff',
                        borderRadius: plan.button?.style?.borderRadius || '8px',
                        padding: plan.button?.style?.padding || '12px 24px',
                        fontSize: plan.button?.style?.fontSize || '16px',
                        fontWeight: plan.button?.style?.fontWeight || '600',
                        border: plan.button?.style?.border || 'none'
                      }}
                    >
                      {plan.button?.text || 'Começar'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // CORRIGIDA: Renderizar seção de contato sem textos padrão
  const renderContactSection = (section, index) => {
    const { content, styles } = section;
    const { title, subtitle, fields, button } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      ...styles
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section contact-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="container">
          <div className="contact-content">
            {/* CORRIGIDO: Só renderiza título se não estiver vazio */}
            {title && title.trim() && (
              <h2 className="contact-title" style={{ color: content.titleStyle?.color || '#333333' }}>
                {title}
              </h2>
            )}

            {/* CORRIGIDO: Só renderiza subtítulo se não estiver vazio */}
            {subtitle && subtitle.trim() && (
              <p className="contact-subtitle" style={{ color: content.subtitleStyle?.color || '#333333' }}>
                {subtitle}
              </p>
            )}
            
            <form className="contact-form" onSubmit={handleContactFormSubmit}>
              {fields?.map((field, fieldIndex) => (
                <div key={fieldIndex} className="form-group">
                  <label htmlFor={field.name}>{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      required={field.required}
                      value={contactFormData[field.name] || ''}
                      onChange={handleContactInputChange}
                      placeholder={`Digite ${field.label.toLowerCase()}`}
                      rows="5"
                    />
                  ) : (
                    <input
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      required={field.required}
                      value={contactFormData[field.name] || ''}
                      onChange={handleContactInputChange}
                      placeholder={`Digite ${field.label.toLowerCase()}`}
                    />
                  )}
                </div>
              ))}
              
              <button 
                type="submit"
                className="contact-cta btn"
                disabled={isSubmitting}
                style={{
                  background: getBackgroundStyle(
                    button?.style?.backgroundColor, 
                    button?.style?.backgroundType, 
                    button?.style?.gradientColors
                  ),
                  color: button?.style?.color || '#ffffff',
                  borderRadius: button?.style?.borderRadius || '8px',
                  padding: button?.style?.padding || '12px 24px',
                  fontSize: button?.style?.fontSize || '16px',
                  fontWeight: button?.style?.fontWeight || '600',
                  border: button?.style?.border || 'none'
                }}
              >
                {isSubmitting ? 'Enviando...' : button?.text || 'Enviar Mensagem'}
              </button>
              
              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('sucesso') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    );
  };

  // CORRIGIDA: Renderizar seção FAQ sem textos padrão
  const renderFaqSection = (section, index) => {
    const { content, styles } = section;
    const { title, subtitle, faqs } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      ...styles
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section faq-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="container">
          <div className="faq-content">
            {/* CORRIGIDO: Só renderiza título se não estiver vazio */}
            {title && title.trim() && (
              <h2 className="faq-title" style={{ color: content.titleStyle?.color || '#333333' }}>
                {title}
              </h2>
            )}

            {/* CORRIGIDO: Só renderiza subtítulo se não estiver vazio */}
            {subtitle && subtitle.trim() && (
              <p className="faq-subtitle" style={{ color: content.subtitleStyle?.color || '#333333' }}>
                {subtitle}
              </p>
            )}
            
            {faqs && faqs.length > 0 && (
              <div className="faq-list">
                {faqs.map((faq, faqIndex) => (
                  <div 
                    key={faqIndex} 
                    className={`faq-item ${openFaq === faqIndex ? 'open' : ''}`}
                    style={{
                      background: getBackgroundStyle(
                        faq.backgroundColor, 
                        faq.backgroundType, 
                        faq.gradientColors
                      ),
                      color: faq.textColor || '#333333'
                    }}
                  >
                    <button 
                      className="faq-question"
                      onClick={() => toggleFaq(faqIndex)}
                      style={{ color: faq.textColor }}
                    >
                      <span>{faq.question}</span>
                      <span className="faq-icon">{openFaq === faqIndex ? '−' : '+'}</span>
                    </button>
                    <div className="faq-answer">
                      <p style={{ color: faq.textColor }}>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // Renderizar seção HTML personalizada
  const renderHtmlSection = (section, index) => {
    const { content, styles } = section;
    const { htmlContent } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      ...styles
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section html-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div 
          className="html-content"
          dangerouslySetInnerHTML={{ __html: htmlContent || '<p>Conteúdo HTML personalizado</p>' }}
        />
      </section>
    );
  };

  // CORRIGIDA: Renderizar seção customizável sem textos padrão
  const renderCustomSection = (section, index) => {
    const { content, styles } = section;
    const { title, subtitle, elements, images, buttons, icons, priceBlocks } = content;
    const sectionId = `section-${section.id || index}`;
    const isAnimated = isVisible[sectionId];

    const sectionStyles = {
      background: getBackgroundStyle(
        styles?.backgroundColor, 
        styles?.backgroundType, 
        styles?.gradientColors
      ),
      position: 'relative',
      ...styles
    };

    const handleCustomButtonClick = (button, buttonIndex) => {
      if (onButtonClick) {
        onButtonClick(button.text, button.link, 'custom', buttonIndex);
      }
    };

    return (
      <section 
        key={section.id}
        id={sectionId}
        className={`section custom-section ${isAnimated ? 'animated' : ''}`}
        style={sectionStyles}
      >
        <div className="container">
          <div className="custom-content">
            {/* CORRIGIDO: Título e Subtítulo só aparecem se não estiverem vazios */}
            {((title && title.trim()) || (subtitle && subtitle.trim())) && (
              <div className="custom-header">
                {title && title.trim() && (
                  <h2 className="custom-title" style={{ color: content.titleStyle?.color || '#333333' }}>
                    {title}
                  </h2>
                )}
                {subtitle && subtitle.trim() && (
                  <p className="custom-subtitle" style={{ color: content.subtitleStyle?.color || '#333333' }}>
                    {subtitle}
                  </p>
                )}
              </div>
            )}

            {/* Elementos de Texto */}
            {elements && elements.length > 0 && (
              <div className="custom-elements">
                {elements.map((element, elementIndex) => (
                  <div
                    key={element.id || elementIndex}
                    className="custom-element"
                    style={{
                      ...element.style,
                      marginBottom: element.style?.marginBottom || '16px'
                    }}
                  >
                    {element.content}
                  </div>
                ))}
              </div>
            )}

            {/* Imagens otimizadas */}
            {images && images.length > 0 && (
              <div className="custom-images">
                {images.map((image, imageIndex) => (
                  <div 
                    key={image.id || imageIndex} 
                    className="custom-image-container"
                    style={{
                      display: 'inline-block',
                      margin: '10px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {image.src ? (
                      <img
                        src={image.src}
                        alt={image.alt || 'Imagem personalizada'}
                        className="custom-image"
                        style={{
                          width: image.style?.width || '100%',
                          height: image.style?.height || 'auto',
                          borderRadius: image.style?.borderRadius || '8px',
                          objectFit: 'cover',
                          ...image.style
                        }}
                        loading="lazy" // Otimização de performance
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div 
                        className="custom-image-placeholder"
                        style={{
                          width: image.style?.width || '200px',
                          height: '150px',
                          background: '#f8f9fa',
                          border: '2px dashed #dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6c757d',
                          fontSize: '0.9rem',
                          borderRadius: '8px'
                        }}
                      >
                        Imagem não encontrada
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Botões Customizáveis */}
            {buttons && buttons.length > 0 && (
              <div className="custom-buttons">
                {buttons.map((button, buttonIndex) => (
                  <button
                    key={button.id || buttonIndex}
                    className="custom-button btn"
                    onClick={() => handleCustomButtonClick(button, buttonIndex)}
                    style={{
                      background: getBackgroundStyle(
                        button.style?.backgroundColor, 
                        button.style?.backgroundType, 
                        button.style?.gradientColors
                      ),
                      color: button.style?.color || '#ffffff',
                      borderRadius: button.style?.borderRadius || '8px',
                      padding: button.style?.padding || '12px 24px',
                      fontSize: button.style?.fontSize || '16px',
                      fontWeight: button.style?.fontWeight || '600',
                      border: button.style?.border || 'none',
                      margin: '8px',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}

            {/* Ícones */}
            {icons && icons.length > 0 && (
              <div className="custom-icons">
                {icons.map((icon, iconIndex) => (
                  <span
                    key={icon.id || iconIndex}
                    className="custom-icon"
                    style={{
                      ...icon.style,
                      display: 'inline-block',
                      margin: '8px'
                    }}
                  >
                    {icon.symbol}
                  </span>
                ))}
              </div>
            )}

            {/* Blocos de Preço */}
            {priceBlocks && priceBlocks.length > 0 && (
              <div className="custom-price-blocks">
                {priceBlocks.map((priceBlock, priceIndex) => (
                  <div
                    key={priceBlock.id || priceIndex}
                    className="custom-price-block"
                    style={{
                      ...priceBlock.style,
                      display: 'inline-block',
                      margin: '16px',
                      textAlign: 'center',
                      minWidth: '200px',
                      maxWidth: '300px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <h4 style={{ 
                      margin: '0 0 8px 0', 
                      color: priceBlock.style?.color,
                      fontSize: '1.2rem'
                    }}>
                      {priceBlock.title}
                    </h4>
                    <div 
                      className="price" 
                      style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: globalStyles?.primaryColor || '#6e48aa',
                        margin: '8px 0'
                      }}
                    >
                      {priceBlock.price}
                    </div>
                    <p style={{ 
                      margin: '8px 0 0 0', 
                      color: priceBlock.style?.color,
                      fontSize: '0.9rem',
                      opacity: 0.8
                    }}>
                      {priceBlock.description}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // Renderizar seção baseada no tipo
  const renderSection = (section, index) => {
    switch (section.type) {
      case 'hero':
        return renderHeroSection(section, index);
      case 'features':
        return renderFeaturesSection(section, index);
      case 'testimonials':
        return renderTestimonialsSection(section, index);
      case 'pricing':
        return renderPricingSection(section, index);
      case 'contact':
        return renderContactSection(section, index);
      case 'faq':
        return renderFaqSection(section, index);
      case 'html':
        return renderHtmlSection(section, index);
      case 'custom':
        return renderCustomSection(section, index);
      default:
        return (
          <div key={section.id} className="section unknown-section">
            <div className="container">
              <p>Tipo de seção desconhecido: {section.type}</p>
            </div>
          </div>
        );
    }
  };

  if (!data || !data.sections) {
    return (
      <div className="landing-page-error">
        <div className="container text-center">
          <h2>Dados da página não encontrados</h2>
          <p>Não foi possível carregar o conteúdo da landing page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page" style={{ scrollBehavior: 'smooth' }}>
      {/* Renderizar todas as seções */}
      {data.sections
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((section, index) => renderSection(section, index))}
    </div>
  );
}

export default LandingPage;